import { useCallback, useMemo, useState } from "react";
import {
  addSession,
  removeWeekAt,
  togglePlacement as togglePlacementInDraft,
  cloneBlock,
  cloneWeekInDraft,
  deriveBlocks,
  removeBlock,
  removeSession,
  setCycleWeeks,
  addWeekCloning,
  type Block,
  type BlockMember,
  type ComposedBlock,
  type DayPanelEntry,
} from "@heraion/shared";

import { useProgramEditor } from "../ProgramEditor/useProgramEditor";
import type { CatalogItem, PersistedSnapshot } from "../ProgramEditor/types";
import type { EditorDraftBlob } from "../ProgramEditor/editorDraftStore";
import type { ProgramComposerProps } from "./types";
import {
  removeBlockOnDay,
  type SlugConflict,
} from "./composeBlocks";
import { useRampEdit } from "./useRampEdit";
import { useComposerCommit } from "./useComposerCommit";
import { useComposerFormMutations } from "./useComposerFormMutations";

export type { SlugConflict } from "./composeBlocks";
export type CloneWeekUiResult =
  | { ok: true }
  | { ok: false; reason: "empty" }
  | {
      ok: false;
      reason: "conflict";
      conflict: { exerciseId: string; dayIdx: number; week: number };
    };

// Composer state: the Draft (truth, same model + save path as the 3b
// editor via useProgramEditor) plus the block lens over it (deriveBlocks)
// and the one-block-at-a-time compose form. Every commit goes through the
// P1 block layer (applyBlock / updateBlock / removeBlock), whose jest
// identity guards carry the §3.2 lossless round-trip. The ramp/per-week
// edit machine lives in useRampEdit (500-line budget).

export type ComposerForm = {
  members: BlockMember[];
  grouped: boolean;
  days: Set<number>;
  weeks: Set<number>;
  // The derived block being edited (✎); null = composing a new block.
  editing: ComposedBlock | null;
  // CD-1 — the day-panel day this edit was opened from. When set, the edit is
  // day-scoped: commit rewrites only `days` (seeded to {editingDay}, widenable
  // via the D-chips), never the block's sibling placements on other days. null
  // while composing a new block (no day to scope to).
  editingDay: number | null;
};

const allOf = (n: number): Set<number> =>
  new Set(Array.from({ length: n }, (_, i) => i));

export function useComposer({
  durableWrite,
  program,
  creationSeed,
  catalog,
  onSaved,
  mode,
  plan,
  planSeed,
  unitsPreference,
  authoredAs,
  onPersisted,
  keepOpen,
  resumeFrom,
  verifyVersion,
}: Pick<
  ProgramComposerProps,
  | "keepOpen"
  | "durableWrite"
  | "program"
  | "creationSeed"
  | "catalog"
  | "onSaved"
  | "plan"
  | "planSeed"
  | "unitsPreference"
  | "authoredAs"
> & {
  // #76: see useProgramEditor.onPersisted.
  onPersisted?: (saved: PersistedSnapshot) => void;
  // #336: see useProgramEditor.resumeFrom.
  resumeFrom?: EditorDraftBlob | null;
  // S5: see useProgramEditor.verifyVersion.
  verifyVersion?: (id: string) => Promise<boolean>;
  // Narrower than ProgramComposerProps.mode on purpose: this hook feeds
  // useProgramEditor, which only ever authors a Program or a Plan.
  // `mode: "prescribe"` (PE-22 K2.1) never reaches here — ComposerBody
  // always calls this with a literal "program", and PlanComposer's own
  // mount (not this hook) handles "single".
  mode?: "program" | "single";
}) {
  const editor = useProgramEditor({
    durableWrite,
    program,
    creationSeed,
    onSaved,
    mode,
    plan,
    planSeed,
    unitsPreference,
    authoredAs,
    onPersisted,
    keepOpen,
    resumeFrom,
    verifyVersion,
  });
  const { draft, setDraft } = editor;

  const blocks = useMemo(() => deriveBlocks(draft), [draft]);

  const dayCount = draft.sessions.length;
  const weekCount = draft.cycleWeeks;

  const emptyForm = useCallback(
    (): ComposerForm => ({
      members: [],
      grouped: false,
      days: allOf(dayCount),
      weeks: allOf(weekCount),
      editing: null,
      editingDay: null,
    }),
    [dayCount, weekCount],
  );
  const [form, setForm] = useState<ComposerForm>(emptyForm);

  // Duplicate-exercise guard outcome of the last commit attempt; the
  // form renders it inline next to ＋AGGIUNGI.
  const [formConflict, setFormConflict] = useState<SlugConflict | null>(null);

  // `editingDay` (CD-1) pins the edit to the day the panel entry was opened
  // from: days seed to {editingDay} so the commit rewrites only that day, not
  // the block's sibling placements on other (e.g. cloned) days. null preserves
  // the legacy block-wide load (every placement day) — used only where no day
  // context exists.
  const startEdit = useCallback(
    (block: ComposedBlock, editingDay: number | null = null) => {
      setFormConflict(null);
      setForm({
        members: block.members.map((m) => ({
          ...m,
          setRows: m.setRows.map((r) => ({ ...r })),
        })),
        grouped: block.members.length > 1,
        days:
          editingDay != null
            ? new Set([editingDay])
            : new Set(block.placements.map((p) => p.dayIdx)),
        weeks: new Set(block.weeks),
        editing: block,
        editingDay,
      });
    },
    [],
  );

  const rampApi = useRampEdit({ blocks, form, setForm, startEdit, weekCount });
  const { weekEdit, formDirty, clearRamp } = rampApi;

  const nameOf = useCallback(
    (slug: string): string | undefined =>
      catalog.find((c: CatalogItem) => c.slug === slug)?.name,
    [catalog],
  );

  // Form-CONTENT mutations (members, rows, day/week placement) live in
  // useComposerFormMutations (500-line budget, the same split that carved out
  // useRampEdit and useComposerCommit). It is handed only setForm and nameOf,
  // so as written it touches the form and never the draft — which is the line
  // between it and the structural/scheduler edits below.
  const formMutations = useComposerFormMutations({ setForm, nameOf });

  // --- block commits ------------------------------------------------------
  // The commit/canCommit machine lives in useComposerCommit (500-line budget),
  // the same split that carved out useRampEdit. All the block-layer data path
  // (findSlugConflict / composeBlocks / applyBlockEditScoped / applyWeekEditScoped)
  // moves with it; this hook just binds it to the composer's form + draft.
  const { canCommit, commit } = useComposerCommit({
    blocks,
    form,
    weekEdit,
    setDraft,
    setForm,
    setFormConflict,
    emptyForm,
    allowDuplicateSlugs: mode === "program",
  });

  // ⧉ Clona settimana — pure column copy in shared (cloneWeekInDraft):
  // the 5/3/1 vertical fill. Computed on the CURRENT draft so the
  // conflict answer is synchronous for the bar's inline message.
  const cloneWeek = useCallback(
    (fromWeek: number, toWeeks: number[]): CloneWeekUiResult => {
      const r = cloneWeekInDraft(draft, fromWeek, toWeeks);
      if (!r.ok) return r;
      setDraft(() => r.draft);
      return { ok: true };
    },
    [draft, setDraft],
  );

  // PE-11 — structural edits (add/remove training day, resize the cycle).
  // Pure transforms in shared (addSession/removeSession/setCycleWeeks); these
  // just bind them to the draft. cycleWeeks grow copies the last week.
  const addDay = useCallback(
    (name: string) => setDraft((d) => addSession(d, name)),
    [setDraft],
  );
  const removeDay = useCallback(
    (idx: number) => setDraft((d) => removeSession(d, idx)),
    [setDraft],
  );
  const changeCycleWeeks = useCallback(
    // PE-45 — `grow` says what an ADDED week inherits. The editor's `W ＋` tap
    // asks for "blank"; the default keeps the shared primitive's historical
    // meaning for anyone who does not ask.
    (weeks: number, grow: "clone" | "blank" = "clone") =>
      setDraft((d) => setCycleWeeks(d, weeks, grow)),
    [setDraft],
  );

  // PE-45 — hold `W ＋`: one more week, repeating the one on screen.
  //
  // ⛔ ONE updater, because the two halves are not independent: the copy reads
  // the draft the growth produced, and `cloneWeek` above closes over the draft
  // from BEFORE it. The composition is pure and lives in shared
  // (`addWeekCloning`), so it is testable without a mount and there is still
  // exactly one definition of what copying a week means.
  //
  // ⚠️ A source week with nothing in it falls back to an empty week instead of
  // doing nothing. Repeating an empty week IS an empty week — and a hold that
  // silently does nothing reads as a broken control, which is the failure this
  // gesture was added to avoid.
  const addWeekRepeating = useCallback(
    (fromWeek: number) =>
      setDraft((d) => {
        const r = addWeekCloning(d, fromWeek);
        return r.ok ? r.draft : setCycleWeeks(d, d.cycleWeeks + 1, "blank");
      }),
    [setDraft],
  );

  // ⧉ Clona: pre-load as a NEW block with the same content (§5.3 — the
  // progression gesture: retarget the weeks, tweak the loads, Add).
  //
  // The kind check below is a type narrowing, NOT a live guard, and it is
  // worth saying so: read on its own it looks like "cloning a verbatim block
  // silently does nothing", which would be a bug, because the only caller
  // (useEditInPlace cloneCurrent) clears editingIdx straight afterwards
  // whether or not this returned early — the editor would detach from the
  // open block while the form kept the old content. That cannot happen.
  // form.editing is typed ComposedBlock | null and is only ever populated
  // through startEdit, itself gated on kind === "composed"
  // (useEditInPlace.ts:76), and cloneBlock returns a hardcoded
  // kind: "composed" for every non-verbatim input (programBlocksExpand.ts:308).
  // So a verbatim block never reaches here and the early return never fires.
  // Do not "fix" the caller to match it; if the day comes that a verbatim
  // block CAN be edited in place, the decision to make is a product one
  // (clone it, disable the gesture, or say why it is unavailable).
  const startClone = useCallback((block: Block) => {
    const cloned = cloneBlock(block);
    if (cloned.kind !== "composed") return;
    setForm({
      members: cloned.members,
      grouped: cloned.members.length > 1,
      days: new Set(cloned.placements.map((p) => p.dayIdx)),
      weeks: new Set(cloned.weeks),
      editing: null,
      editingDay: null,
    });
  }, []);

  const removeBlockFromDraft = useCallback(
    (block: Block) => {
      setDraft((d) => removeBlock(d, block));
      setForm((f) => (f.editing === block ? emptyForm() : f));
    },
    [setDraft, emptyForm],
  );

  // ✕ from the day panel: a merged entry removes every week segment of the
  // exercise on THIS day (the whole exercise as the program means it). CD-1 —
  // day-scoped: on a fused multi-day block only the opened day's placement is
  // removed, never the sibling (e.g. cloned) days; `dayIdx` is the panel day
  // the ✕ was tapped on.
  const removeEntry = useCallback(
    (entry: DayPanelEntry, dayIdx: number) => {
      const toRemove = entry.kind === "composed" ? entry.blocks : [entry.block];
      setDraft((d) => {
        let next = d;
        for (const b of toRemove) next = removeBlockOnDay(next, b, dayIdx);
        return next;
      });
      clearRamp();
      setForm(emptyForm());
    },
    [setDraft, emptyForm, clearRamp],
  );

  // PE-22 F — one cell of the weeks x days matrix, written STRAIGHT into the
  // draft: the hold replaced the staged D/W chips, so there is no commit left
  // to arm. Pure write only — the caller owns the flush before and the reopen
  // after (useEditInPlace.togglePlacementAt), because BOTH are required and
  // neither belongs to the draft:
  //
  //  · flush first, or `commit()` recomposes placement from the form's stale
  //    day/week sets and silently UNDOES the hold — no error, no red gate, the
  //    cell just springs back;
  //  · reopen after, because a non-rectangular placement cannot live in one
  //    block (weeks is block-wide, placements is per day) and deriveBlocks
  //    SPLITS it — leaving form.editing on a block that no longer exists.
  const togglePlacement = useCallback(
    (block: ComposedBlock, dayIdx: number, week: number) => {
      setDraft((d) => togglePlacementInDraft(d, block, dayIdx, week));
    },
    [setDraft],
  );

  // The − on a week row. Structural like addDay/removeDay, so it writes the
  // draft straight: the two-tap guard lives on the control, not here.
  const removeWeek = useCallback(
    (week: number) => setDraft((d) => removeWeekAt(d, week)),
    [setDraft],
  );

  const resetForm = useCallback(() => {
    clearRamp();
    setFormConflict(null);
    setForm(emptyForm());
  }, [emptyForm, clearRamp]);

  return {
    ...editor,
    ...rampApi,
    blocks,
    dayCount,
    weekCount,
    form,
    formDirty,
    formConflict,
    canCommit,
    ...formMutations,
    commit,
    cloneWeek,
    addDay,
    removeDay,
    changeCycleWeeks,
    addWeekRepeating,
    startEdit,
    startClone,
    removeBlockFromDraft,
    removeEntry,
    resetForm,
    togglePlacement,
    removeWeek,
  };
}

export type Composer = ReturnType<typeof useComposer>;
